import {objectFromEntries} from '@benchling/js-utils';

import {
  HasOwnablePermissions,
  HasPoliceablePermissions,
  MinimalFolderPermissions,
  Permissions,
  PoliceableInfo,
} from 'auth/PermissionConstants';
import {BasicPermissionsGraphQL} from 'auth/PermissionConstants';
import {OwnableInfo} from 'create-components/OwnableConstants';
import {PermissionsNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  ORG_COLLABORATOR,
  ORG_SCHEMA_COLLABORATOR,
  USER_2_COLLABORATOR,
  USER_2_SCHEMA_COLLABORATOR,
} from 'test/fixtures/CollaboratorFixtures';
import {USER} from 'test/fixtures/parties/UserFixtures';
import omitKey from 'util/omitKey';

import {CollaboratorInfoGraphQL} from '../../../graphql-fragments/auth/__generated/CollaboratorInfoGraphQL';
import {HasOwnablePermissionsGraphQL} from '../../../graphql-fragments/auth/__generated/HasOwnablePermissionsGraphQL';
import {ListingPermissionsGraphQL} from '../../../graphql-fragments/auth/__generated/ListingPermissionsGraphQL';
import {OwnerAndCollaboratorInfoGraphQL} from '../../../graphql-fragments/auth/__generated/OwnerAndCollaboratorInfoGraphQL';
import {NavStoreSourceGraphQL} from '../../../graphql-fragments/nav/__generated/NavStoreSourceGraphQL';
import {RegistryItemListingPermissionsGraphQL} from '../../../graphql-fragments/registry/registry-items/__generated/RegistryItemListingPermissionsGraphQL';
import {SchemaPermissionsGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';

type NavStoreRegistryPermissions = ExtractByTypename<NavStoreSourceGraphQL, 'RegistryNode'>['permissions'];

type NavStoreProjectPermissions = ExtractByTypename<NavStoreSourceGraphQL, 'ProjectNode'>['permissions'];

type NavStoreSourcePermissionsGraphQL = NavStoreProjectPermissions | NavStoreRegistryPermissions;

export function getLegacyRESTPermissions<T extends BasicPermissionsGraphQL>(gqlPermissions: T): Permissions {
  const permissions = omitKey(gqlPermissions, '__typename');
  return {
    ...permissions,
    ...objectFromEntries(Object.entries(permissions).map(([k, v]) => [k, Boolean(v)])),
  };
}

export const READ_ONLY_PERMISSIONS: Permissions = {
  readable: true,
  writable: false,
  appendable: false,
  admin: false,
  polymerFeaturesEditable: false,
  polymerResiduesEditable: false,
  unregisterable: false,
  registryIdsChangeable: false,
  definitionEditable: false,
  canEditNamesAndBarcodes: false,
};

export const READ_ONLY_PERMISSIONS_GQL: BasicPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: false,
  readable: true,
  writable: false,
};

export const READ_ONLY_LISTING_PERMISSIONS_GQL: ListingPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: false,
  archivable: false,
  deletable: false,
  movable: false,
  readable: true,
  unarchivable: false,
  writable: false,
};

export const READ_ONLY_REGISTRY_ITEM_LISTING_PERMISSIONS_GQL: RegistryItemListingPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: false,
  archivable: false,
  canEditNamesAndAliases: false,
  deletable: false,
  movable: false,
  polymerFeaturesEditable: false,
  polymerResiduesEditable: false,
  readable: true,
  registryIdsChangeable: false,
  unarchivable: false,
  unregisterable: false,
  writable: false,
};

export const APPENDABLE_PERMISSIONS: Permissions = {
  readable: true,
  writable: false,
  appendable: true,
  admin: false,
};

export const WRITABLE_PERMISSIONS: Permissions = {
  admin: false,
  appendable: true,
  archivable: true,
  canEditBarcodes: true,
  canEditNamesAndAliases: true,
  canEditNamesAndBarcodes: true,
  definitionEditable: true,
  deletable: true,
  movable: true,
  polymerFeaturesEditable: true,
  polymerResiduesEditable: true,
  readable: true,
  registryIdsChangeable: true,
  subfolderCreatable: true,
  unarchivable: false,
  unregisterable: true,
  writable: true,
};

export const WRITABLE_PERMISSIONS_GQL: BasicPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: true,
  readable: true,
  writable: true,
};

export const WRITABLE_LISTING_PERMISSIONS_GQL: ListingPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: true,
  archivable: true,
  deletable: true,
  movable: true,
  readable: true,
  unarchivable: false,
  writable: true,
};

export const WRITABLE_REGISTRY_ITEM_LISTING_PERMISSIONS_GQL: RegistryItemListingPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: true,
  archivable: true,
  canEditNamesAndAliases: true,
  deletable: true,
  movable: null,
  polymerFeaturesEditable: true,
  polymerResiduesEditable: true,
  readable: true,
  registryIdsChangeable: true,
  unarchivable: false,
  unregisterable: true,
  writable: true,
};

export const WRITABLE_REGISTRY_ITEM_LISTING_PERMISSIONS_REST: Permissions = getLegacyRESTPermissions(
  WRITABLE_REGISTRY_ITEM_LISTING_PERMISSIONS_GQL
);

export const WRITABLE_PERMISSIONS_FOR_POLICEABLE: Permissions = {
  readable: true,
  writable: true,
  appendable: true,
  admin: false,
  definitionEditable: true,
};

export const ADMIN_PERMISSIONS_FOR_POLICEABLE: Permissions = {
  readable: true,
  writable: true,
  appendable: true,
  admin: true,
  definitionEditable: true,
};

interface PoliceablePermissionsGraphQL {
  __typename: 'PermissionsNode';
  readable: boolean;
  writable: boolean;
  appendable: boolean;
  admin: boolean;
  definitionEditable: boolean;
}

export const WRITABLE_PERMISSIONS_FOR_POLICEABLE_GQL: PoliceablePermissionsGraphQL = {
  __typename: 'PermissionsNode',
  readable: true,
  writable: true,
  appendable: true,
  admin: false,
  definitionEditable: true,
};

export const ADMIN_PERMISSIONS_FOR_POLICEABLE_GQL: PoliceablePermissionsGraphQL = {
  __typename: 'PermissionsNode',
  readable: true,
  writable: true,
  appendable: true,
  admin: true,
  definitionEditable: true,
};

export const WRITABLE_WORKLIST_PERMISSIONS: Permissions = {
  readable: true,
  writable: true,
  appendable: true,
  admin: false,
  owner: true,
};

export const WRITABLE_WORKLIST_PERMISSIONS_GQL: HasOwnablePermissionsGraphQL['permissions'] = {
  __typename: 'PermissionsNode',
  readable: true,
  writable: true,
  appendable: true,
  admin: false,
  owner: true,
};

export const HAS_WRITABLE_WORKLIST_PERMISSIONS: HasOwnablePermissions = {
  id: 'wkl_12345',
  permissions: WRITABLE_WORKLIST_PERMISSIONS,
};

export const HAS_WRITABLE_WORKLIST_PERMISSIONS_GQL: HasOwnablePermissionsGraphQL = {
  __typename: 'WorklistNode',
  id: 'wkl_12345',
  permissions: WRITABLE_WORKLIST_PERMISSIONS_GQL,
};

export const HAS_ADMIN_CONTAINER_WORKLIST_PERMISSIONS: HasOwnablePermissions = {
  id: 'wkl_12345',
  permissions: {
    ...WRITABLE_WORKLIST_PERMISSIONS,
    admin: true,
  },
};

export const WRITABLE_TAG_SCHEMA_PERMISSIONS: HasPoliceablePermissions = {
  id: 'ts_12345',
  permissions: WRITABLE_PERMISSIONS_FOR_POLICEABLE,
};

export const ADMIN_TAG_SCHEMA_PERMISSIONS: HasPoliceablePermissions = {
  id: 'ts_12345',
  permissions: ADMIN_PERMISSIONS_FOR_POLICEABLE,
};

export const ARCHIVED_PERMISSIONS: Permissions = {
  readable: true,
  writable: false,
  appendable: true,
  admin: false,
  archivable: false,
  unarchivable: true,
};

export const DELETABLE_PERMISSIONS: Permissions = {
  readable: true,
  writable: false,
  appendable: false,
  admin: false,
  archivable: false,
  unarchivable: true,
  deletable: true,
};

export const NON_ARCHIVABLE_PERMISSIONS: Permissions = {
  readable: true,
  writable: false,
  appendable: true,
  admin: false,
  archivable: false,
  unarchivable: false,
};

export const POLYMER_FEATURES_EDITABLE_PERMISSIONS: Permissions = {
  readable: true,
  writable: false,
  appendable: false,
  admin: false,
  polymerFeaturesEditable: true,
  polymerResiduesEditable: false,
};

export const SUBFOLDER_NOT_CREATABLE_PERMISSIONS: Permissions = {
  readable: true,
  writable: true,
  appendable: true,
  admin: false,
  subfolderCreatable: false,
};

export const WRITABLE_FOLDER_PERMISSIONS: Permissions = {
  readable: true,
  writable: true,
  appendable: true,
  admin: false,
  subfolderCreatable: true,
  bioentitiesAppendable: true,
  batchesWritable: true,
};

export const WRITABLE_WITHOUT_EDIT_NAMES_AND_BARCODES_PERMISSIONS: Permissions = {
  ...WRITABLE_PERMISSIONS,
  canEditNamesAndBarcodes: false,
  canEditBarcodes: false,
};

export const APPENDABLE_FOLDER_PERMISSIONS: Permissions = {
  readable: true,
  writable: false,
  appendable: true,
  admin: false,
  subfolderCreatable: false,
  bioentitiesAppendable: true,
  batchesWritable: true,
};

export const ADMIN_PERMISSIONS: Permissions = {
  readable: true,
  writable: true,
  appendable: true,
  admin: true,
};

export const DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_GQL: NavStoreSourcePermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: true,
  archivable: true, // note that this key could be 'unarchivable' instead depending on the state of 'archived'
  batchesWritable: true,
  bioentitiesAppendable: true,
  canCreateRequest: true,
  canManageCollaborators: false,
  deletable: true,
  locationsCreatable: false,
  movable: false,
  owner: true,
  readable: true,
  subfolderCreatable: true,
  unarchivable: false,
  writable: true,
};

export const DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_REST: Permissions = getLegacyRESTPermissions(
  DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_GQL
);

export const SAMPLE_RESTRICTION_ADMIN_PERMISSIONS: Permissions = {
  ...ADMIN_PERMISSIONS,
  canRestrictSamples: true,
  canUnrestrictSamples: true,
  canEditRestrictedSampleUsers: true,
};

export const MINIMAL_FOLDER_PERMISSIONS: MinimalFolderPermissions = {
  subfolderCreatable: true,
};

export const COLLABORATOR_INFO: PoliceableInfo = {
  id: 'ts_12345',
  name: 'my tag schema',
  collaborators: [],
};

export const COLLABORATOR_INFO_GQL: CollaboratorInfoGraphQL = {
  __typename: 'TagSchemaNode',
  id: 'ts_12345',
  name: 'my tag schema',
  collaboratorsJson: [],
};

export const OWNER_AND_COLLABORATOR_INFO: OwnableInfo = {
  id: 'wkl_12345',
  name: 'my worklist',
  owner: USER,
  ownerInfo: {
    type: 'user',
    memberDataPolicyId: null,
  },
  collaborators: [],
};

export const OWNER_AND_COLLABORATOR_INFO_GQL: OwnerAndCollaboratorInfoGraphQL = {
  __typename: 'WorklistNode',
  id: 'wkl_12345',
  name: 'my worklist',
  ownerJson: {
    ...USER,
  },
  ownerInfoJson: {
    type: 'user',
    memberDataPolicyId: null,
  },
  collaboratorsJson: [],
};

export const OWNER_AND_EXISTING_COLLABORATOR_INFO: OwnableInfo = {
  id: 'wkl_23456',
  name: 'my worklist',
  owner: USER,
  ownerInfo: {
    type: 'user',
    memberDataPolicyId: null,
  },
  collaborators: [USER_2_COLLABORATOR, ORG_COLLABORATOR],
};

export const EXISTING_SCHEMA_COLLABORATOR_INFO: PoliceableInfo = {
  id: 'ts_12345',
  name: 'my tag schema',
  collaborators: [USER_2_SCHEMA_COLLABORATOR, ORG_SCHEMA_COLLABORATOR],
};

/**
 * In nearly every situation, it would be preferrable to use a narrower set of permissions, defined by either
 * a specific query or fragment relevant to the UI being rendered. This is a catch-all for when that is not
 * possible and should be avoided.
 */
export const FULL_PERMISSIONS_GRAPHQL: PermissionsNode = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: false,
  archivable: null,
  batchesWritable: null,
  bioentitiesAppendable: null,
  canAccept: null,
  canAddRemoveAdmins: null,
  canAddRemoveEditors: null,
  canAddRemoveViewers: null,
  canCreateRequest: null,
  canCreateTeams: null,
  canEditBarcodes: null,
  canEditNamesAndAliases: null,
  canEditNamesAndBarcodes: null,
  canEditRestrictedSampleUsers: null,
  canExecuteRequest: null,
  canManageCollaborators: null,
  canManageRoles: null,
  canReassignRlcReviewer: null,
  canReject: null,
  canRestrictSamples: null,
  canRetract: null,
  canSendForReview: null,
  canUnrestrictSamples: null,
  definitionDeletable: null,
  definitionEditable: null,
  definitionListable: null,
  deletable: null,
  locationsCreatable: null,
  movable: null,
  objectsCreatable: null,
  objectsRegisterable: null,
  owner: null,
  polymerFeaturesEditable: null,
  polymerResiduesEditable: null,
  readable: false,
  registryIdsChangeable: null,
  subfolderCreatable: null,
  tagsEditable: null,
  unarchivable: null,
  unregisterable: null,
  writable: false,
  canEditStatus: null,
  canEditContainer: null,
};

export const SCHEMA_PERMISSIONS: Permissions = {
  admin: false,
  appendable: true,
  archivable: true,
  definitionDeletable: true,
  definitionEditable: true,
  definitionListable: true,
  objectsCreatable: true,
  objectsRegisterable: true,
  readable: true,
  unarchivable: false,
  writable: true,
};

export const SCHEMA_ADMIN_PERMISSIONS: Permissions = {
  ...SCHEMA_PERMISSIONS,
  admin: true,
};

export const SCHEMA_PERMISSIONS_GQL: SchemaPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: true,
  archivable: true,
  definitionDeletable: true,
  definitionEditable: true,
  definitionListable: true,
  objectsCreatable: true,
  objectsRegisterable: true,
  readable: true,
  unarchivable: false,
  writable: true,
};

export const SCHEMA_ADMIN_PERMISSIONS_GQL: SchemaPermissionsGraphQL = {
  ...SCHEMA_PERMISSIONS_GQL,
  admin: true,
};

export const READ_ONLY_SCHEMA_PERMISSIONS_GQL: SchemaPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: false,
  archivable: false,
  definitionDeletable: false,
  definitionEditable: false,
  definitionListable: true,
  objectsCreatable: false,
  objectsRegisterable: false,
  readable: true,
  unarchivable: false,
  writable: false,
};
